import React from "react";
import { Card, Row, Col, Statistic, Progress, Tag, Empty, Tooltip } from "antd";
import { getChartTheme } from "../../../theme/chart-theme";
import { useThemeMode } from "../../../theme/theme-provider";
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
    orderStatusMeta, fmtMoney, fmtNumber, fmtShortMoney, totalsByStatus,
} from "./reward-shared-meta";

/**
 * Tab Tổng quan 1 đợt: thẻ số liệu + biểu đồ theo LÔ NGÀY MUA.
 * Số liệu lấy từ sổ RewardOrders (không phải cột usedBudget) — lệch nhau
 * là dấu hiệu job settle có vấn đề, admin thấy ngay ở đây.
 */
function RewardOverviewTab({ program, overview }) {
    // recharts nhận màu qua prop, không ăn theo antd ConfigProvider
    const { isDark } = useThemeMode();
    const chart = getChartTheme(isDark);
    const byStatus = totalsByStatus(overview?.statusTotals || []);
    const participants = overview?.participants || {};
    const num = (v) => Number(v || 0);

    const reservedAmount = num(byStatus.reserved?.amount);
    const awardedAmount = num(byStatus.awarded?.amount);
    const usedFromLedger = reservedAmount + awardedAmount; // đơn còn hiệu lực
    const refAmount = num(byStatus.reserved?.refAmount) + num(byStatus.awarded?.refAmount);
    const totalBudget = program?.totalBudget != null ? Number(program.totalBudget) : null;
    const budgetPct = totalBudget ? Math.min(100, Math.round((usedFromLedger / totalBudget) * 100)) : 0;
    const totalOrders = Object.values(byStatus).reduce((s, t) => s + num(t.count), 0);
    // Lệch giữa sổ và cột vận hành — chỉ cảnh báo khi thực sự khác.
    const usedBudgetDrift = program?.usedBudget != null ? usedFromLedger - Number(program.usedBudget) : 0;

    const chartData = (overview?.daily || []).map((d) => ({
        ...d,
        joins: (overview?.joins || []).find((j) => j.date === d.date)?.joins || 0,
    }));

    return (
        <div className="flex flex-col gap-4">
            <Row gutter={[12, 12]}>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic title="Người đăng ký" value={participants.total || 0} />
                        <div className="text-xs text-content-3 mt-1">
                            {fmtNumber(participants.buyers)} người đã có đơn ·{" "}
                            <span className="text-orange-500">{fmtNumber(participants.noOrder)} chưa có đơn</span>
                        </div>
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic title="Đơn ghi sổ" value={totalOrders} />
                        <div className="text-xs text-content-3 mt-1">
                            {fmtNumber(num(byStatus.reserved?.count) + num(byStatus.awarded?.count))} đơn có thưởng ·{" "}
                            <span className="text-orange-500">{fmtNumber(byStatus.out_of_budget?.count)} trượt</span>
                        </div>
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic title="Đã cộng ví" value={awardedAmount} formatter={fmtMoney} />
                        <div className="text-xs text-content-3 mt-1">
                            Đang giữ suất: {fmtMoney(reservedAmount)}
                        </div>
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic title="Ref của thưởng" value={refAmount} formatter={fmtMoney} />
                        <div className="text-xs text-content-3 mt-1">Ngoài ngân sách đợt</div>
                    </Card>
                </Col>
            </Row>

            <Card size="small" title="Ngân sách">
                {totalBudget == null ? (
                    <span className="text-content-3">Đợt không giới hạn ngân sách</span>
                ) : (
                    <>
                        <Progress percent={budgetPct} status={budgetPct >= 100 ? "exception" : "active"} />
                        <div className="text-sm text-content-2">
                            Đã dùng <b>{fmtMoney(usedFromLedger)}</b> / {fmtMoney(totalBudget)} · còn{" "}
                            <b>{fmtMoney(Math.max(0, totalBudget - usedFromLedger))}</b>
                        </div>
                    </>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                    {Object.entries(orderStatusMeta(program)).map(([key, meta]) => (
                        <Tag key={key} color={meta.color}>
                            {meta.label}: {fmtNumber(byStatus[key]?.count)} đơn · {fmtMoney(byStatus[key]?.amount)}
                        </Tag>
                    ))}
                    {usedBudgetDrift !== 0 && (
                        <Tooltip title="Tổng từ sổ RewardOrders khác cột usedBudget của đợt — kiểm tra job chốt lô">
                            <Tag color="red">Lệch sổ: {fmtMoney(usedBudgetDrift)}</Tag>
                        </Tooltip>
                    )}
                </div>
            </Card>

            <Card size="small" title="Diễn biến theo lô ngày mua">
                {chartData.length === 0 ? (
                    <Empty description="Chưa có dòng sổ nào" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={chartData} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                            <XAxis dataKey="date" tick={{ fontSize: 11, fill: chart.axis }} stroke={chart.axis} tickLine={false} />
                            <YAxis yAxisId="money" tickFormatter={fmtShortMoney} tick={{ fontSize: 11, fill: chart.axis }} stroke={chart.axis} width={52} axisLine={false} tickLine={false} />
                            <YAxis yAxisId="count" orientation="right" tick={{ fontSize: 11, fill: chart.axis }} stroke={chart.axis} width={40} axisLine={false} tickLine={false} />
                            <ReTooltip
                                formatter={(value, name) => [name === "Tiền thưởng" ? fmtMoney(value) : fmtNumber(value), name]}
                            />
                            <Legend wrapperStyle={{ color: chart.axis }} />
                            <Bar yAxisId="money" dataKey="reward" name="Tiền thưởng" fill={chart.series[1]} radius={[3, 3, 0, 0]} />
                            <Line yAxisId="count" type="monotone" dataKey="orders" name="Số đơn" stroke={chart.series[2]} strokeWidth={2} dot={false} />
                            <Line yAxisId="count" type="monotone" dataKey="missed" name="Đơn trượt" stroke={chart.series[3]} strokeWidth={2} dot={false} />
                            <Line yAxisId="count" type="monotone" dataKey="joins" name="Lượt đăng ký" stroke={chart.series[4]} strokeWidth={2} strokeDasharray="4 3" dot={false} />
                        </ComposedChart>
                    </ResponsiveContainer>
                )}
            </Card>
        </div>
    );
}

export default RewardOverviewTab;
